// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {LMModel} from '@form/model';
import {LMFormLayoutService} from '@form/services';
import {LMLayoutUtilDirective} from './lm-layout-util.directive';
import {LMLoggerService} from '@app/services';

enum LMParagraphSubType {
  CHECK_CONTROL = 0,
  BUTTON,
  FIELD,
  TEXT,
  MENU,
  MANUAL_REPRESENTATION,
  CAPTCHA_CONTROL
}

@Component({
  selector:    'lm-paragraph',
  templateUrl: 'lm-paragraph-layout.component.html',
  styleUrls:   [ 'lm-paragraph-layout.component.scss' ],
  providers: [ LMLayoutUtilDirective, LMFormLayoutService ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMParagraphLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() layoutObject: LMFormParagraphLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() placeHolder: any;
  @Input() layoutInfo: LMFormLayoutInfo;
  @Input() lmColumnLayoutInfo: LMColumnLayoutInfo;
  @Input() parentIsCompoundField: boolean;
  @Input() parentCompoundFieldDisplayAsText: boolean;
  @Input() parentCompoundFieldDisplayOnly: boolean;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo = {};

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() errorOnMemberField: EventEmitter<string> = new EventEmitter<string>();

  @ViewChildren('Element') elements: QueryList<LMFormLayoutElement>;
  @ViewChild('LeftElementWrapper') leftElementWrapper: ElementRef;
  @ViewChild('CenterElementWrapper') centerElementWrapper: ElementRef;
  @ViewChild('RightElementWrapper') rightElementWrapper: ElementRef;

  // Only has a single child of text area or rich text
  // Use these classes to span the text area 100% width of form
  @HostBinding('class.lm-textarea-only') isTextAreaOnly: boolean;
  @HostBinding('class.lm-rich-text-only') isRichTextOnly: boolean;

  @HostBinding('class.lm-distributed') isDistributed: boolean;

  @HostListener('window:resize', ['$event']) onResize() {
    this.handleResize();
  }

  // Flag used to set textArea field to display block if it is the only child
  // This will allow the textArea to span the full width of the page in this case
  // public textAreaOnly = false;
  public numberOfElements: number;
  public hasFieldsWithAlignment: boolean;
  public layoutSubType: { readonly [x: number]: string; CHECK_CONTROL: any; BUTTON: any; FIELD: any; TEXT: any; MENU: any; MANUAL_REPRESENTATION: any; CAPTCHA_CONTROL: any };
  public readonly instanceId: string;

  constructor(
    protected lmFormLayoutService: LMFormLayoutService,
    private ngZone: NgZone,
    private lmLoggerService: LMLoggerService
  ) {
    this.layoutSubType = LMParagraphSubType;
    this.instanceId = this.lmLoggerService.instanceId;
  }

  ngOnInit() {
    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: `Total-Paragraph-Layout-${this.instanceId}`,
        description: 'TOTAL PARAGRAPH LAYOUT',
        functionName: 'ngOnInit()',
        className: 'LMParagraphLayoutComponent',
        timingStart: 'LMParagraphLayoutComponent -> ngOnInit()',
        timingEnd: 'LMParagraphLayoutComponent -> ngAfterViewInit()',
      });
    }

    if (this.layoutObject.centerElements || this.layoutObject.rightElements) {
      this.hasFieldsWithAlignment = true;
    }

    // If the only alignment used is left align then render using the default paragraph layout
    // Renders the same way as if there was no alignment
    if (!this.hasFieldsWithAlignment && (this.layoutObject.leftElements && this.layoutObject.leftElements.length > 0)) {
      this.layoutObject.elements = this.layoutObject.leftElements;
    }

    if (this.lmParagraphLayoutInfo.previewMode) {
      this.lmParagraphLayoutInfo.displayAsText = true;
    }

    if (this.layoutObject.distributed) {
      this.lmParagraphLayoutInfo.isDistributed = true;
      this.isDistributed = true;
    }

    // If column is parent show labels for paragraph layouts
    // This will make sure paragraph layouts still align horizontally with non paragraph layouts in other columns.
    // Need this to be determined after the rest of lmParagraphLayoutInfo is filled out
    const hasVisibleLabels = this.lmFormLayoutService.paragraphHasVisibleLabels(this.layoutObject, this.model, this.specFields, this.lmParagraphLayoutInfo);
    this.lmParagraphLayoutInfo.hasVisibleLabels = this.lmColumnLayoutInfo !== undefined ? true : hasVisibleLabels;

    // Check if paragraph consists of a single text area or a single rich text field
    this.isTextAreaOnly = this.lmParagraphLayoutInfo.sectionInfo?.paragraphHasTextAreaOnly;
    this.isRichTextOnly = this.lmParagraphLayoutInfo.sectionInfo?.paragraphHasRichTextOnly;

    // Fields need to be sized using width instead of size if paragraph layout is nested in a compound field
    // Passing this information down to the field components
    this.lmParagraphLayoutInfo.isCompoundField = this.parentIsCompoundField;
    this.lmParagraphLayoutInfo.displayAsText = this.parentCompoundFieldDisplayAsText;
    this.lmParagraphLayoutInfo.displayOnly = this.parentCompoundFieldDisplayOnly;
    this.lmParagraphLayoutInfo.textAreaOnly = this.isTextAreaOnly;
    this.lmParagraphLayoutInfo.parentIsParagraphLayout = true;

    if (this.layoutObject.elements) {
      this.setLayoutType(this.layoutObject.elements);
    }

    if (this.layoutObject.leftElements) {
      this.setLayoutType(this.layoutObject.leftElements);
    }

    if (this.layoutObject.centerElements) {
      this.setLayoutType(this.layoutObject.centerElements);
    }

    if (this.layoutObject.rightElements) {
      this.setLayoutType(this.layoutObject.rightElements);
    }
  }

  ngAfterViewInit() {
    this.numberOfElements = this.ngZone.runOutsideAngular(() => this.elements.toArray().length);

    if (this.hasFieldsWithAlignment) {
      this.handleResize();
    }

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.timeEnd(`Total-Paragraph-Layout-${this.instanceId}`);
    }
  }

  ngOnDestroy() {}

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  onMemberFieldError(event) {
    this.errorOnMemberField.emit(event);
  }

  public trackByIndex(index: number, element: LMFormLayoutElement): number {
    return element.index;
  }

  private setLayoutType(elements: Array<LMFormLayoutElement>) {
    elements.forEach((element: LMFormLayoutElement, i) => {
      element.index = i;
      if (element.checkcontrol) {
        element.elementLayout = this.layoutSubType.CHECK_CONTROL;
      } else if (element.button) {
        element.elementLayout = this.layoutSubType.BUTTON;
      } else if (element.field) {
        element.elementLayout = this.layoutSubType.FIELD;
      } else if (element.text) {
        element.elementLayout = this.layoutSubType.TEXT;
      } else if (element.menu) {
        element.elementLayout = this.layoutSubType.MENU;
      } else if (element.manualrep) {
        element.elementLayout = this.layoutSubType.MANUAL_REPRESENTATION;
      } else if (element.captchaControl) {
        element.elementLayout = this.layoutSubType.CAPTCHA_CONTROL;
      }
    });
  }

  /**
   * Align items to the left when they wrap
   * @private
   */
  private handleResize(): void {
    if (this.leftElementWrapper && this.centerElementWrapper && this.rightElementWrapper) {
      const leftWrapperOffset = this.leftElementWrapper.nativeElement.offsetLeft;
      const centerWrapperOffset = this.centerElementWrapper.nativeElement.offsetLeft;
      const rightWrapperOffset = this.rightElementWrapper.nativeElement.offsetLeft;

      this.centerElementWrapper.nativeElement.style.textAlign =  centerWrapperOffset === leftWrapperOffset ? 'left' : '';
      this.rightElementWrapper.nativeElement.style.textAlign = rightWrapperOffset === leftWrapperOffset ? 'left' : '';
    }
  }
}
